import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Tag,
    Shirt,
    Shield,
    Hammer,
    Package,
    HardHat,
    Zap,
    Wind,
    Thermometer,
    Droplets,
    Construction,
    AlertTriangle,
    Check
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';

interface AddCategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    category?: any;
}

const ICONS = [
    { name: 'Shirt', component: Shirt },
    { name: 'Shield', component: Shield },
    { name: 'HardHat', component: HardHat },
    { name: 'Hammer', component: Hammer },
    { name: 'Package', component: Package },
    { name: 'Zap', component: Zap },
    { name: 'Wind', component: Wind },
    { name: 'Thermometer', component: Thermometer },
    { name: 'Droplets', component: Droplets },
    { name: 'Construction', component: Construction },
    { name: 'AlertTriangle', component: AlertTriangle },
    { name: 'Tag', component: Tag },
];

const COLORS = [
    { name: 'Blue', value: 'blue', bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100', hex: '#3b82f6' },
    { name: 'Orange', value: 'orange', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100', hex: '#f97316' },
    { name: 'Green', value: 'green', bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-100', hex: '#22c55e' },
    { name: 'Purple', value: 'purple', bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-100', hex: '#a855f7' },
    { name: 'Red', value: 'red', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100', hex: '#ef4444' },
    { name: 'Cyan', value: 'cyan', bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-100', hex: '#06b6d4' },
    { name: 'Yellow', value: 'yellow', bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-100', hex: '#eab308' },
    { name: 'Pink', value: 'pink', bg: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-100', hex: '#ec4899' },
    { name: 'Slate', value: 'slate', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-100', hex: '#64748b' },
];

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({ isOpen, onClose, onRefresh, category }) => {
    const [formData, setFormData] = useState({
        name: '',
        icon: 'Tag',
        color: 'blue',
        isActive: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name || '',
                icon: category.icon || 'Tag',
                color: category.color || 'blue',
                isActive: category.isActive !== undefined ? category.isActive : true
            });
        } else {
            setFormData({
                name: '',
                icon: 'Tag',
                color: 'blue',
                isActive: true
            });
        }
    }, [category, isOpen]);

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Category name is required');
            return;
        }

        try {
            setLoading(true);
            if (category) {
                await api.patch(`/api/inventory/categories/${category.id}`, formData);
                toast.success('Category updated successfully');
            } else {
                await api.post('/api/inventory/categories', formData);
                toast.success('Category created successfully');
            }
            onRefresh();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save category');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-[32px] p-0 overflow-hidden border-none shadow-2xl">
                <div className="p-6 sm:p-8 space-y-6 max-h-[90vh] overflow-y-auto custom-scrollbar">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 text-xl sm:text-2xl font-bold">
                            <div className={cn(
                                "w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-colors",
                                COLORS.find(c => c.value === formData.color)?.bg || "bg-blue-50"
                            )}>
                                {(() => {
                                    const IconComp = ICONS.find(i => i.name === formData.icon)?.component || Tag;
                                    return <IconComp size={20} className={COLORS.find(c => c.value === formData.color)?.text || "text-blue-600"} />;
                                })()}
                            </div>
                            {category ? 'Edit Category' : 'Add New Category'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* Name Field */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Category Name</label>
                            <div className="relative group">
                                <Tag size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" />
                                <Input
                                    placeholder="e.g., Personal Protective Equipment"
                                    className="pl-12 rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-medium"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Icon Selection */}
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Select Icon</label>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 sm:gap-2">
                                {ICONS.map((icon) => (
                                    <button
                                        key={icon.name}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, icon: icon.name })}
                                        className={cn(
                                            "aspect-square rounded-xl flex items-center justify-center transition-all border",
                                            formData.icon === icon.name
                                                ? "bg-slate-900 border-slate-900 text-white shadow-lg"
                                                : "bg-gray-50 border-transparent text-gray-400 hover:bg-gray-100"
                                        )}
                                    >
                                        <icon.component size={18} className="sm:w-5 sm:h-5" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Color Selection */}
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Select Color</label>
                            <div className="flex flex-wrap gap-2.5">
                                {COLORS.map((color) => (
                                    <button
                                        key={color.value}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, color: color.value })}
                                        className={cn(
                                            "w-8 h-8 rounded-full border-2 transition-all flex items-center justify-center shadow-sm hover:scale-110",
                                            formData.color === color.value ? "border-slate-900 scale-110" : "border-transparent"
                                        )}
                                        style={{ backgroundColor: color.hex }}
                                    >
                                        {formData.color === color.value && <Check size={14} className="text-white" />}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Status Toggle */}
                        <div className="flex items-center justify-between p-4 bg-gray-50/50 border border-gray-100 rounded-2xl group hover:border-blue-100 transition-all">
                            <div>
                                <p className="text-sm font-bold text-gray-800">Active Status</p>
                                <p className="text-[10px] text-gray-400 font-medium">Allow this category to be assigned to items</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                                className={cn(
                                    "w-10 h-5 rounded-full relative transition-all duration-300",
                                    formData.isActive ? "bg-blue-500" : "bg-gray-200"
                                )}
                            >
                                <div className={cn(
                                    "absolute top-1 w-3 h-3 bg-white rounded-full transition-all duration-300 shadow-sm",
                                    formData.isActive ? "left-6" : "left-1"
                                )} />
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button
                            variant="outline"
                            className="order-2 sm:order-1 flex-1 rounded-2xl h-12 font-bold border-gray-100 hover:bg-gray-50"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="order-1 sm:order-2 flex-1 rounded-2xl h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold flex items-center justify-center gap-2 shadow-lg shadow-slate-900/10"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                category ? 'Update Category' : 'Create Category'
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddCategoryModal;
